import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = {
  key: 0,
  class: "fas fa-spinner fa-spin"
}
const _hoisted_3 = {
  key: 1,
  class: "fas fa-save"
}
const _hoisted_4 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-8" }
const _hoisted_5 = { class: "bg-white p-6 rounded-xl border border-gray-200 shadow-sm" }
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_7 = { class: "md:col-span-2 mb-6" }
const _hoisted_8 = { class: "relative" }
const _hoisted_9 = {
  key: 0,
  class: "mt-1 text-xs text-red-600"
}
const _hoisted_10 = { class: "relative" }
const _hoisted_11 = {
  key: 0,
  class: "mt-1 text-xs text-red-600"
}
const _hoisted_12 = { class: "bg-white p-6 rounded-xl border border-gray-200 shadow-sm" }
const _hoisted_13 = { class: "flex items-center justify-between mb-4" }
const _hoisted_14 = {
  for: "sameAddress",
  class: "inline-flex items-center gap-2 text-sm font-medium text-black"
}
const _hoisted_15 = ["checked"]
const _hoisted_16 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_17 = { class: "md:col-span-2 mb-6" }
const _hoisted_18 = { class: "relative" }
const _hoisted_19 = {
  key: 0,
  class: "mt-1 text-xs text-black"
}
const _hoisted_20 = { class: "relative" }
const _hoisted_21 = {
  key: 0,
  class: "mt-1 text-xs text-black"
}
const _hoisted_22 = { class: "relative md:col-span-2" }
const _hoisted_23 = { class: "relative" }
const _hoisted_24 = ["value"]
const _hoisted_25 = {
  key: 0,
  class: "text-sm text-red-500 mt-2 flex items-center"
}
const _hoisted_26 = {
  key: 1,
  class: "text-sm text-green-600 mt-2 flex items-center"
}
const _hoisted_27 = {
  key: 2,
  class: "text-sm text-black mt-2"
}
const _hoisted_28 = { class: "relative" }
const _hoisted_29 = {
  key: 0,
  class: "mt-1 text-xs text-red-600"
}
const _hoisted_30 = {
  key: 1,
  class: "mt-1 text-xs text-black"
}

import { DataCard, BaseInput, AddressAutocompleteField } from '@/components'
import { centerOptions } from '@/data'
import { computed, onMounted, watch, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useAdminEfpStore } from '@/stores/adminEfp'

import type { GeneralInformationFormData } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'AddressesSection',
  props: {
    formData: {},
    saving: { type: Boolean },
    disableSave: { type: Boolean }
  },
  emits: ["update:formData", "validation-changed", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const authStore = useAuthStore()
const adminEfp = useAdminEfpStore()

const canEditSiretTva = computed(
  () => authStore.isImpersonating && adminEfp.canAccess('edit-siret-tva'),
)

onMounted(() => {
  if (authStore.isImpersonating && !adminEfp.isAdminEfpValidated) {
    void adminEfp.validate()
  }
})

// Regex de validation
const phoneRegex = /^(0[1-9](\d{8})|\+33[1-9](\d{8}))$/

// États de validation
const phoneError = computed(() => {
  if (!props.formData.storeAddress.phone) return false
  return !phoneRegex.test(props.formData.storeAddress.phone)
})

const mobileError = computed(() => {
  if (!props.formData.storeAddress.mobile) return false
  return !phoneRegex.test(props.formData.storeAddress.mobile)
})

const phoneBillingError = computed(() => {
  if (!props.formData.billingAddress.phone) return false
  return !phoneRegex.test(props.formData.billingAddress.phone)
})

// État local pour EORI
const eoriValue = ref(props.formData.billingAddress.eori || '')
const eoriHasError = ref(false)
const eoriIsValid = ref(false)
const eoriErrorMessage = ref('')

// Gestion EORI avec blocage automatique
const handleEoriInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  const cleanValue = value.replace(/\s/g, '')
  
  if (cleanValue.length > 17) {
    return // Bloque si on dépasse 17 caractères
  }
  
  // Blocage strict : si on a exactement 17 caractères, on ne peut plus taper
  if (cleanValue.length >= 17) {
    const finalValue = cleanValue.substring(0, 17).toUpperCase()
    eoriValue.value = finalValue
    updateFormData('billingAddress.eori', finalValue)
    validateEori()
    return
  }
  
  eoriValue.value = value.toUpperCase()
  updateFormData('billingAddress.eori', value.toUpperCase())
  validateEori()
}

// Validation EORI
// Format EORI : 2 lettres (code pays) + jusqu'à 15 caractères alphanumériques (max 17 caractères total)
const validateEori = () => {
  const value = eoriValue.value.trim()
  
  if (!value) {
    eoriHasError.value = false
    eoriIsValid.value = false
    eoriErrorMessage.value = ''
    return
  }
  
  const cleanValue = value.replace(/\s/g, '').toUpperCase()
  // Format EORI : 2 lettres (code pays ISO) + 1 à 15 caractères alphanumériques
  const eoriRegex = /^[A-Z]{2}[A-Z0-9]{1,15}$/
  
  if (cleanValue.length < 3 || cleanValue.length > 17) {
    eoriHasError.value = true
    eoriIsValid.value = false
    eoriErrorMessage.value = 'Format EORI invalide. Exemple : FR123456789012'
    return
  }
  
  if (!eoriRegex.test(cleanValue)) {
    eoriHasError.value = true
    eoriIsValid.value = false
    eoriErrorMessage.value = 'Format EORI invalide. Exemple : FR123456789012'
    return
  }
  
  eoriHasError.value = false
  eoriIsValid.value = true
  eoriErrorMessage.value = ''
}

// Validation globale - détecte toutes les erreurs
const hasValidationErrors = computed(() => {
  // Erreurs de format téléphones
  const phoneFormatErrors = phoneError.value || mobileError.value || phoneBillingError.value
  
  // Erreurs de téléphones obligatoires
  const phoneRequiredErrors = !props.formData.storeAddress.phone || 
                             !props.formData.storeAddress.mobile ||
                             (!props.formData.billingAddress.sameAsStore && !props.formData.billingAddress.phone)
  
  // Erreurs de champs requis pour l'adresse du magasin
  const requiredStoreFields = !props.formData.storeAddress.storeName || 
                              !props.formData.storeAddress.street || 
                              !props.formData.storeAddress.city || 
                              !props.formData.storeAddress.postalCode
  
  // Erreurs de champs requis pour l'adresse de facturation (si pas "même adresse")
  const requiredBillingFields = !props.formData.billingAddress.sameAsStore && 
                               (!props.formData.billingAddress.company ||
                                !props.formData.billingAddress.street ||
                                !props.formData.billingAddress.city ||
                                !props.formData.billingAddress.postalCode)
  
  // Société toujours obligatoire même si "même adresse"
  const companyRequired = !props.formData.billingAddress.company
  
  return !!(phoneFormatErrors || phoneRequiredErrors || requiredStoreFields || requiredBillingFields || companyRequired)
})

const updateFormData = (field: string, value: any) => {
  const keys = field.split('.')

  const updatedFormData = {
    ...props.formData,
    [keys[0]]: { ...(props.formData as any)[keys[0]] }
  }

  let current: any = updatedFormData
  for (let i = 0; i < keys.length - 1; i++) {
    current = current[keys[i]]
  }

  current[keys[keys.length - 1]] = value
  emit('update:formData', updatedFormData)
}



// Utilitaire: copie l'adresse du magasin vers la facturation
// (évite d'émettre si aucune donnée ne change pour prévenir les boucles)
const copyStoreToBilling = () => {
  const s = props.formData.storeAddress
  const b = props.formData.billingAddress

  const noChange =
    b.number === s.number &&
    b.street === s.street &&
    b.city === s.city &&
    b.postalCode === s.postalCode &&
    b.phone === s.phone

  if (noChange) return

  // émettre uniquement si une valeur diffère
  const updatedFormData = {
    ...props.formData,
    billingAddress: {
      ...props.formData.billingAddress,
      number: s.number,
      street: s.street,
      city: s.city,
      postalCode: s.postalCode,
      phone: s.phone,
    },
  }
  emit('update:formData', updatedFormData)
}

const handleSameAsStoreChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked
  
  // Mettre à jour la checkbox
  updateFormData('billingAddress.sameAsStore', isChecked)
  
  // Si coché, copier les valeurs de l'adresse du magasin
  if (isChecked) {
    copyStoreToBilling()
  }
}

const handleStoreAddressSelected = (address: { streetAddress: string; postalCode: string; city: string; fullAddress: string }) => {
  const updatedFormData = { ...props.formData }
  updatedFormData.storeAddress.street = address.streetAddress
  updatedFormData.storeAddress.postalCode = address.postalCode
  updatedFormData.storeAddress.city = address.city
  emit('update:formData', updatedFormData)
}

const handleBillingAddressSelected = (address: { streetAddress: string; postalCode: string; city: string; fullAddress: string }) => {
  const updatedFormData = { ...props.formData }
  updatedFormData.billingAddress.street = address.streetAddress
  updatedFormData.billingAddress.postalCode = address.postalCode
  updatedFormData.billingAddress.city = address.city
  emit('update:formData', updatedFormData)
}

// Watcher pour émettre les changements de validation
watch(hasValidationErrors, (hasErrors) => {
  emit('validation-changed', hasErrors)
}, { immediate: true })

// Synchroniser automatiquement l'adresse de facturation quand "Adresse identique" est cochée
// Utilise des sources multiples pour éviter de recréer un objet et déclencher des mises à jour inutiles
watch(
  [
    () => props.formData.billingAddress.sameAsStore,
    () => props.formData.storeAddress.street,
    () => props.formData.storeAddress.city,
    () => props.formData.storeAddress.postalCode,
    () => props.formData.storeAddress.phone,
    () => props.formData.storeAddress.number,
  ],
  (newVals) => {
    const same = newVals[0] as boolean
    if (same) {
      copyStoreToBilling()
    }
  }
)

// Synchronisation avec les props pour EORI
watch(() => props.formData.billingAddress.eori, (newValue) => {
  eoriValue.value = newValue || ''
  if (newValue) {
    validateEori()
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(DataCard), {
    title: "Adresses",
    icon: "fas fa-map-marker-alt",
    "icon-color": "text-green-600",
    "icon-bg-color": "bg-green-100",
    class: "w-full"
  }, {
    "header-actions": _withCtx(() => [
      _createElementVNode("button", {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('save'))),
        disabled: _ctx.saving || _ctx.disableSave,
        class: "px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      }, [
        (_ctx.saving)
          ? (_openBlock(), _createElementBlock("i", _hoisted_2))
          : (_openBlock(), _createElementBlock("i", _hoisted_3)),
        _createElementVNode("span", null, _toDisplayString(_ctx.saving ? 'Sauvegarde...' : 'Sauvegarder'), 1)
      ], 8, _hoisted_1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _cache[18] || (_cache[18] = _createElementVNode("div", { class: "flex items-center gap-3 mb-4" }, [
            _createElementVNode("div", { class: "w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center" }, [
              _createElementVNode("i", { class: "fas fa-store text-black text-sm" })
            ]),
            _createElementVNode("h3", { class: "text-lg font-semibold text-black" }, "Adresse du magasin")
          ], -1)),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.storeAddress.storeName,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (updateFormData('storeAddress.storeName', $event))),
              label: "Nom de l'enseigne",
              type: "text",
              placeholder: "Nom de votre enseigne",
              required: ""
            }, null, 8, ["model-value"]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(AddressAutocompleteField), {
                "model-value": _ctx.formData.storeAddress.street,
                "postal-code": _ctx.formData.storeAddress.postalCode,
                city: _ctx.formData.storeAddress.city,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = (value) => updateFormData('storeAddress.street', value)),
                "onUpdate:postalCode": _cache[3] || (_cache[3] = (value) => updateFormData('storeAddress.postalCode', value)),
                "onUpdate:city": _cache[4] || (_cache[4] = (value) => updateFormData('storeAddress.city', value)),
                onAddressSelected: handleStoreAddressSelected
              }, null, 8, ["model-value", "postal-code", "city"])
            ]),
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.storeAddress.complement,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (updateFormData('storeAddress.complement', $event))),
              label: "Complément d'adresse",
              type: "text",
              placeholder: "Complément"
            }, null, 8, ["model-value"]),
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.storeAddress.center,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (updateFormData('storeAddress.center', $event))),
              label: "Centre",
              type: "select",
              options: _unref(centerOptions)
            }, null, 8, ["model-value", "options"]),
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.storeAddress.building,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (updateFormData('storeAddress.building', $event))),
              label: "N° Bâtiment",
              type: "text",
              placeholder: "N° Bâtiment"
            }, null, 8, ["model-value"]),
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.storeAddress.lot,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (updateFormData('storeAddress.lot', $event))),
              label: "Lot",
              type: "text",
              placeholder: "Lot"
            }, null, 8, ["model-value"]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(BaseInput), {
                "model-value": _ctx.formData.storeAddress.phone,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => (updateFormData('storeAddress.phone', $event))),
                label: "Téléphone",
                type: "tel",
                placeholder: "Téléphone",
                required: "",
                class: _normalizeClass([
                'transition-colors',
                phoneError.value ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              ])
              }, null, 8, ["model-value", "class"]),
              (phoneError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Format téléphone invalide. Exemple : 0123456789 ou +33123456789 "))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_unref(BaseInput), {
                "model-value": _ctx.formData.storeAddress.mobile,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => (updateFormData('storeAddress.mobile', $event))),
                label: "Mobile",
                type: "tel",
                placeholder: "Mobile",
                required: "",
                class: _normalizeClass([
                'transition-colors',
                mobileError.value ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              ])
              }, null, 8, ["model-value", "class"]),
              (mobileError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, " Format mobile invalide. Exemple : 0123456789 ou +33123456789 "))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _cache[20] || (_cache[20] = _createElementVNode("div", { class: "flex items-center gap-3" }, [
              _createElementVNode("div", { class: "w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center" }, [
                _createElementVNode("i", { class: "fas fa-file-invoice text-black text-sm" })
              ]),
              _createElementVNode("h3", { class: "text-lg font-semibold text-black" }, "Adresse de facturation")
            ], -1)),
            _createElementVNode("label", _hoisted_14, [
              _createElementVNode("input", {
                checked: _ctx.formData.billingAddress.sameAsStore,
                onChange: handleSameAsStoreChange,
                type: "checkbox",
                id: "sameAddress",
                class: "w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
              }, null, 40, _hoisted_15),
              _cache[19] || (_cache[19] = _createElementVNode("span", null, "Adresse identique", -1))
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_unref(BaseInput), {
              "model-value": _ctx.formData.billingAddress.company,
              "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => (updateFormData('billingAddress.company', $event))),
              label: "Société",
              type: "text",
              placeholder: "Nom de la société",
              required: ""
            }, null, 8, ["model-value"]),
            _createElementVNode("div", _hoisted_17, [
              _createVNode(_unref(AddressAutocompleteField), {
                "model-value": _ctx.formData.billingAddress.street,
                "postal-code": _ctx.formData.billingAddress.postalCode,
                city: _ctx.formData.billingAddress.city,
                "onUpdate:modelValue": _cache[12] || (_cache[12] = (value) => updateFormData('billingAddress.street', value)),
                "onUpdate:postalCode": _cache[13] || (_cache[13] = (value) => updateFormData('billingAddress.postalCode', value)),
                "onUpdate:city": _cache[14] || (_cache[14] = (value) => updateFormData('billingAddress.city', value)),
                onAddressSelected: handleBillingAddressSelected,
                disabled: _ctx.formData.billingAddress.sameAsStore,
                class: _normalizeClass({ 'opacity-50': _ctx.formData.billingAddress.sameAsStore })
              }, null, 8, ["model-value", "postal-code", "city", "disabled", "class"])
            ]),
            _createElementVNode("div", _hoisted_18, [
              _createVNode(_unref(BaseInput), {
                "model-value": _ctx.formData.billingAddress.siret,
                "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => (updateFormData('billingAddress.siret', $event))),
                label: "SIRET",
                type: "text",
                placeholder: "Numéro SIRET",
                disabled: !canEditSiretTva.value,
                class: _normalizeClass({ 'opacity-50': !canEditSiretTva.value })
              }, null, 8, ["model-value", "disabled", "class"]),
              (!canEditSiretTva.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, " Pour modifier le SIRET, contactez le service client "))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_20, [
              _createVNode(_unref(BaseInput), {
                "model-value": _ctx.formData.billingAddress.tva,
                "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => (updateFormData('billingAddress.tva', $event))),
                label: "N° TVA",
                type: "text",
                placeholder: "Numéro TVA",
                disabled: !canEditSiretTva.value,
                class: _normalizeClass({ 'opacity-50': !canEditSiretTva.value })
              }, null, 8, ["model-value", "disabled", "class"]),
              (!canEditSiretTva.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, " Pour modifier le numéro TVA, contactez le service client "))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_22, [
              _cache[24] || (_cache[24] = _createElementVNode("label", { class: "block text-sm font-medium text-black mb-2" }, [
                _createTextVNode(" Numéro EORI "),
                _createElementVNode("span", { class: "text-black text-xs ml-2" }, "(optionnel)")
              ], -1)),
              _createElementVNode("div", _hoisted_23, [
                _createElementVNode("input", {
                  type: "text",
                  placeholder: "FR123456789012",
                  value: eoriValue.value,
                  onInput: handleEoriInput,
                  onBlur: validateEori,
                  maxlength: 17,
                  class: _normalizeClass(["w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200", eoriHasError.value ? 'border-red-500 bg-red-50' : eoriIsValid.value && eoriValue.value ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'])
                }, null, 42, _hoisted_24),
                (eoriHasError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_25, [
                      _cache[21] || (_cache[21] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-2" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(eoriErrorMessage.value), 1)
                    ]))
                  : (eoriIsValid.value && eoriValue.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_26, _cache[22] || (_cache[22] = [
                        _createElementVNode("i", { class: "fas fa-check-circle mr-2" }, null, -1),
                        _createTextVNode(" Format EORI valide ")
                      ])))
                    : (_openBlock(), _createElementBlock("p", _hoisted_27, _cache[23] || (_cache[23] = [
                        _createElementVNode("i", { class: "fas fa-info-circle mr-2" }, null, -1),
                        _createTextVNode(" Numéro d'identification douanière (obligatoire pour les expéditions hors UE) ")
                      ])))
              ])
            ]),
            _createElementVNode("div", _hoisted_28, [
              _createVNode(_unref(BaseInput), {
                "model-value": _ctx.formData.billingAddress.phone,
                "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => (updateFormData('billingAddress.phone', $event))),
                label: "Téléphone",
                type: "tel",
                placeholder: "Téléphone",
                disabled: _ctx.formData.billingAddress.sameAsStore,
                required: !_ctx.formData.billingAddress.sameAsStore,
                class: _normalizeClass([
                'transition-colors',
                phoneBillingError.value ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
                _ctx.formData.billingAddress.sameAsStore ? 'opacity-50' : ''
              ])
              }, null, 8, ["model-value", "disabled", "required", "class"]),
              (phoneBillingError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_29, " Format téléphone invalide. Exemple : 0123456789 ou +33123456789 "))
                : (_ctx.formData.billingAddress.phone && !phoneBillingError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_30, " Format : 0123456789 ou +33123456789 "))
                  : _createCommentVNode("", true)
            ])
          ])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})